import React, { useEffect, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import dayjs from "dayjs";
import api from "../../api/api";
import jsPDF from "jspdf";

const EmployeeDayModal = ({
  employee,
  selectedDay,
  dateRange,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [recap, setRecap] = useState([]);
  const [absentDates, setAbsentDates] = useState([]);
  const [ptoDates, setPtoDates] = useState([]);
  const [view, setView] = useState("table");

  const practiseid = window.getPracticeId();

  useEffect(() => {
    if (employee && dateRange) {
      fetchRecapData();
    }
  }, [employee, dateRange]);

  const fetchRecapData = async () => {
    try {
      setLoading(true);

      const startDate = dayjs(dateRange.startDate).format("YYYY-MM-DD");
      const endDate = dayjs(dateRange.endDate).format("YYYY-MM-DD");

      const response = await api.get(`/v1/${practiseid}/timesheet/recap`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          emp_id: employee.id,
        },
      });

      if (response.status === 200 && response.data?.length) {
        const empData = response.data[0];

        setRecap(empData.recap || []);
        setAbsentDates(empData.absent_dates || []);

        const ptoRanges =
          empData.pto_details?.map((p) => ({
            start: dayjs(p.start_date),
            end: dayjs(p.end_date),
          })) || [];

        setPtoDates(ptoRanges);
      } else {
        setRecap([]);
        setAbsentDates([]);
        setPtoDates([]);
      }
    } catch (err) {
      console.error("Error fetching employee recap:", err);
      setRecap([]);
      setAbsentDates([]);
      setPtoDates([]);
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  // ✅ Filter only selected weekday
  const filteredRecap = recap.filter(
    (day) => dayjs(day.date).format("dddd") === selectedDay
  );

  // --- Calendar logic ---
  const start = dayjs(dateRange.startDate);
  const end = dayjs(dateRange.endDate);

  const daysInRange = [];

  for (
    let d = start;
    d.isBefore(end) || d.isSame(end, "day");
    d = d.add(1, "day")
  ) {
    daysInRange.push(d.format("YYYY-MM-DD"));
  }

  const weeks = [];
  let currentWeek = [];

  daysInRange.forEach((date) => {
    const weekday = dayjs(date).day();

    if (currentWeek.length === 0 && weekday !== 0) {
      for (let i = 0; i < weekday; i++) {
        currentWeek.push(null);
      }
    }

    currentWeek.push(date);

    if (weekday === 6) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  // ================= PDF =================

  const generatePDF = (employee, recapData) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    const startX = 14;

    let startY = 20;

    doc.setFontSize(16);

    doc.text(
      `${employee.name} - ${selectedDay} Timesheet`,
      pageWidth / 2,
      startY,
      { align: "center" }
    );

    startY += 10;

    const headers = ["Date", "Clock In", "Clock Out", "Total Hours"];

    doc.setFontSize(10);

    let x = startX;

    headers.forEach((header) => {
      doc.text(header, x, startY);
      x += 45;
    });

    startY += 6;

    doc.line(startX, startY, pageWidth - startX, startY);

    startY += 4;

    recapData.forEach((day) => {
      const dateStr = `${dayjs(day.date).format(
        "YYYY-MM-DD"
      )} (${dayjs(day.date).format("ddd")})`;

      day.entries?.forEach((entry) => {
        let x = startX;

        const clockIn = entry.clock_in_time
          ? dayjs(`2000-01-01 ${entry.clock_in_time}`).format("HH:mm")
          : "-";

        const clockOut = entry.clock_out_time
          ? dayjs(`2000-01-01 ${entry.clock_out_time}`).format("HH:mm")
          : "-";

        const formatHours = (timeStr) => {
          if (!timeStr) return "-";

          const [h = 0, m = 0] = timeStr.split(":").map(Number);

          if (h === 0 && m === 0) return "0:00";

          if (h === 0) return `0:${String(m).padStart(2, "0")}`;

          if (m === 0) return `${h}:00`;

          return `${h}:${String(m).padStart(2, "0")}`;
        };

        const row = [
          dateStr,
          clockIn,
          clockOut,
          formatHours(day.total_hours),
        ];

        row.forEach((cell) => {
          doc.text(String(cell), x, startY);
          x += 45;
        });

        startY += 6;

        if (startY > 280) {
          doc.addPage();
          startY = 20;
        }
      });
    });

    // ✅ Grand Total
    let totalSeconds = 0;

    recapData.forEach((day) => {
      const [h = 0, m = 0, s = 0] = (
        day.total_hours || "0:00:00"
      )
        .split(":")
        .map(Number);

      totalSeconds += h * 3600 + m * 60 + s;
    });

    const totalHrs = Math.floor(totalSeconds / 3600);

    const totalMins = Math.floor((totalSeconds % 3600) / 60);

    const grandTotal = `${totalHrs}:${String(totalMins).padStart(
      2,
      "0"
    )}`;

    doc.line(startX, startY, pageWidth - startX, startY);

    startY += 6;

    doc.setFontSize(11);

    doc.text("Grand Total:", startX, startY);

    doc.text(grandTotal, startX + 135, startY);

    doc.save(
      `${employee.name.replace(" ", "_")}_${selectedDay}_Timesheet.pdf`
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white w-[950px] max-h-[90vh] rounded-lg shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-5 py-3 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {employee.name} - {selectedDay}
            </h2>

            <p className="text-sm text-gray-500">
              {employee.role}
            </p>

          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl font-light"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-3 overflow-y-auto flex-grow">
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="250px"
            >
              <CircularProgress
                size={45}
                thickness={4}
                color="primary"
              />
            </Box>
          ) : (
            <div className="mt-2">
              <div className="flex justify-between items-center">
                <h6 className="text-gray-700 font-semibold mb-2">
                  {selectedDay} Timesheet Details : &nbsp;  
                    {dayjs(dateRange.startDate).format("MMM DD")} -{" "}
                    {dayjs(dateRange.endDate).format("MMM DD, YYYY")}
                </h6>

                <button
                  onClick={() =>
                    generatePDF(employee, filteredRecap)
                  }
                  className="px-3 py-1 mb-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Download PDF
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th
                        colSpan={6}
                        className="px-3 py-2 text-left text-gray-700"
                      >
                        Employee : {employee.name} (ID:{" "}
                        {employee.id})
                      </th>
                    </tr>

                    <tr>
                      <th className="px-3 py-2 border-b">
                        Date
                      </th>

                      <th className="px-3 py-2 border-b">
                        Clock In
                      </th>

                      <th className="px-3 py-2 border-b">
                        Clock Out
                      </th>

                      <th className="px-3 py-2 border-b">
                        Total Hours
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRecap.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="text-center py-4 text-gray-500"
                        >
                          No {selectedDay} data available
                        </td>
                      </tr>
                    ) : (
filteredRecap.map((day, idx) => {
  const formatHours = (timeStr) => {
    if (!timeStr) return "-";

    const [h = 0, m = 0] = timeStr.split(":").map(Number);

    if (h === 0 && m === 0) return "0";

    if (h === 0) return `${m}`;

    if (m === 0) return `${h}`;

    return `${h}:${String(m).padStart(2, "0")}`;
  };

  return day.entries?.map((entry, eIdx) => (
    <tr key={`${idx}-${eIdx}`} className="text-center">
      
      {/* ✅ Date only once */}
      {eIdx === 0 && (
        <td
          rowSpan={day.entries.length}
          className="px-3 py-2 border-b text-center font-medium"
        >
          {`${dayjs(day.date).format("YYYY-MM-DD")} (${dayjs(
            day.date
          ).format("dddd")})`}
        </td>
      )}

      {/* Clock In */}
      <td className="px-3 py-2 border-b">
        {entry.clock_in_time
          ? dayjs(
              `2000-01-01 ${entry.clock_in_time}`
            ).format("HH:mm")
          : "-"}
      </td>

      {/* Clock Out */}
      <td className="px-3 py-2 border-b">
        {entry.clock_out_time
          ? dayjs(
              `2000-01-01 ${entry.clock_out_time}`
            ).format("HH:mm")
          : "-"}
      </td>

      {/* ✅ Total Hours only once */}
      {eIdx === 0 && (
        <td
          rowSpan={day.entries.length}
          className="px-3 py-2 border-b align-top font-medium text-gray-800"
        >
          {formatHours(day.total_hours)}
        </td>
      )}
    </tr>
  ));
})
                    )}

                    {/* Grand Total */}
                    {filteredRecap.length > 0 &&
                      (() => {
                        let totalSeconds = 0;

                        filteredRecap.forEach((day) => {
                          const [h = 0, m = 0, s = 0] = (
                            day.total_hours || "0:00:00"
                          )
                            .split(":")
                            .map(Number);

                          totalSeconds +=
                            h * 3600 + m * 60 + s;
                        });

                        const hours = Math.floor(
                          totalSeconds / 3600
                        );

                        const minutes = Math.floor(
                          (totalSeconds % 3600) / 60
                        );

                        const display =
                          hours === 0
                            ? `${minutes}`
                            : minutes === 0
                            ? `${hours}`
                            : `${hours}:${minutes}`;

                        return (
                          <tr className="bg-gray-100 font-semibold text-center">
                            <td
                              className="px-3 py-2 border-t text-right"
                              colSpan={3}
                            >
                              Grand Total
                            </td>

                            <td className="px-3 py-2 border-t text-[#50869A]">
                              {display}
                            </td>
                          </tr>
                        );
                      })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDayModal;